import { Mail, Globe, MapPin, Send, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const MAIL_BODY = encodeURIComponent(
  "Name:\nCompany:\nTechnology of interest:\nFeedstock / site / timeline:\n\nMessage:\n"
);

const Contact = () => (
  <div data-testid="contact-page" className="pt-28 sm:pt-36 pb-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Contact us</Eyebrow>
        <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
          Let's engineer your <span className="text-gradient">Net Zero project</span>
        </h1>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-5 gap-12">
        {/* INFO PANEL */}
        <Reveal className="lg:col-span-2">
          <div className="space-y-8">
            <p className="text-gray-300 leading-relaxed">
              Tell us about your feedstock, site or ambition. Our consultancy covers
              concept to creation — installation, commissioning and operation of medium
              to large capacity plants.
            </p>
            <div className="space-y-4">
              <a href="mailto:info@green-planet.tech" data-testid="contact-email-info" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-carbon p-5 hover:border-lime/50 transition-colors group">
                <span className="rounded-full bg-lime/10 p-3"><Mail className="text-lime" size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">General enquiries</span>
                  <span className="text-white group-hover:text-lime transition-colors">info@green-planet.tech</span>
                </span>
              </a>
              <a href="mailto:sureshmir@green-planet.tech" data-testid="contact-email-suresh" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-carbon p-5 hover:border-lime/50 transition-colors group">
                <span className="rounded-full bg-lime/10 p-3"><Mail className="text-lime" size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Direct — Suresh C. Mirchandani</span>
                  <span className="text-white group-hover:text-lime transition-colors">sureshmir@green-planet.tech</span>
                </span>
              </a>
              <div data-testid="contact-address" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-carbon p-5">
                <span className="rounded-full bg-lime/10 p-3"><MapPin className="text-lime" size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Office</span>
                  <span className="text-white">38 Douglas Place, Bedfordview 2008, South Africa</span>
                </span>
              </div>
              <a href="https://www.green-planet.tech" target="_blank" rel="noopener noreferrer" data-testid="contact-website-link" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-carbon p-5 hover:border-lime/50 transition-colors group">
                <span className="rounded-full bg-lime/10 p-3"><Globe className="text-lime" size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Website</span>
                  <span className="text-white group-hover:text-lime transition-colors">www.green-planet.tech</span>
                </span>
              </a>
            </div>
            <div className="rounded-2xl border border-lime/25 bg-lime/5 p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">Green Planet Technologies Pty Ltd</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-gray-500">Reg. No. K2025943399 · South Africa</p>
              <p className="mt-3 text-sm text-gray-300 leading-relaxed">
                Circular economy · Clean energy · Planet friendly · Net Zero ambition.
              </p>
            </div>
          </div>
        </Reveal>

        {/* EMAIL ENQUIRY PANEL */}
        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="h-full rounded-2xl border border-white/10 bg-carbon p-8 sm:p-10 flex flex-col justify-center">
            <Send className="text-lime" size={32} />
            <h2 className="mt-6 font-display font-bold text-2xl sm:text-3xl text-white">
              Enquire by email
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed max-w-lg">
              Tap a button below — your phone's email app will open with everything
              pre-addressed. Tell us your feedstock, location and timeline, and our
              consultancy will respond with a concept pathway.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={`mailto:info@green-planet.tech?subject=${encodeURIComponent("Project enquiry — green-planet.tech")}&body=${MAIL_BODY}`}
                data-testid="contact-enquire-email-button"
                className="inline-flex items-center justify-center gap-2 bg-lime text-obsidian font-semibold px-7 py-4 rounded-full hover:bg-lime-bright hover:shadow-[0_0_28px_rgba(132,204,22,0.4)] transition-all duration-300"
              >
                Email Your Enquiry <Send size={18} />
              </a>
              <a
                href={`mailto:sureshmir@green-planet.tech?subject=${encodeURIComponent("Direct enquiry — green-planet.tech")}&body=${MAIL_BODY}`}
                data-testid="contact-direct-email-button"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-medium px-7 py-4 rounded-full hover:border-lime/60 hover:text-lime transition-all duration-300"
              >
                Email Suresh Directly <ArrowUpRight size={18} />
              </a>
            </div>
            <p className="mt-6 text-xs text-gray-500">
              Prefer to write freely? Our addresses are listed on the left — every enquiry
              is answered personally.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </div>
);

export default Contact;
